import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  Folder,
  List,
  Mail,
  ShieldX,
  Star,
  TrendingUp,
  Unlink,
  Users,
} from "lucide-react";
import { api, type AudienceListView } from "../../lib/api.js";
import { StatCard } from "../../components/ui/StatCard.js";
import { SectionCard } from "../../components/ui/SectionCard.js";
import { Sparkline } from "../../components/ui/Sparkline.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { EmptyState } from "../../components/ui/EmptyState.js";

/**
 * Audience — Brevo contacts + lists from the operator's workspace, viewed
 * live (we proxy to the Brevo MCP per request). The page surfaces:
 *
 *   - 4 KPI tiles (contacts, lists, blacklisted, orphans) with 12-month growth sparkline
 *   - Lists table with reported vs ACTUAL subscriber counts (the Brevo stale-counter bug)
 *   - Drill-down: click a list → contacts panel with paginated email list
 */
export function AudiencePage() {
  const [selectedListId, setSelectedListId] = useState<number | null>(null);

  const overview = useQuery({
    queryKey: ["audience", "overview"],
    queryFn: () => api.getAudienceOverview(),
    refetchInterval: 60_000,
  });

  if (overview.error) {
    return (
      <div className="card p-6 text-sm text-danger">
        <strong>Failed to load Brevo data:</strong> {(overview.error as Error).message}
        <p className="mt-2 text-xs text-muted">
          Check that <code className="rounded bg-line px-1 py-0.5 font-mono">BREVO_API_KEY</code> is set in Credentials.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-ink">Audience</h2>
        <p className="mt-1 text-sm text-muted">
          Live view of your Brevo contacts + lists. The "actual" subscriber count is what Brevo really stores —
          the dashboard's reported total is often stale.
        </p>
      </div>

      {/* KPI strip */}
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Total contacts"
          value={overview.isPending ? "—" : overview.data?.kpis.totalContacts.toLocaleString() ?? 0}
          icon={Users}
          tone="brand"
          hint="across all lists + orphans"
          sparkline={
            overview.data?.kpis.growthSparkline12mo && (
              <Sparkline data={overview.data.kpis.growthSparkline12mo} width={120} height={36} />
            )
          }
        />
        <StatCard
          label="Lists"
          value={overview.isPending ? "—" : overview.data?.kpis.totalLists ?? 0}
          icon={List}
          tone="accent"
          hint="folders + segments"
        />
        <StatCard
          label="Blacklisted (recent)"
          value={overview.isPending ? "—" : overview.data?.kpis.recentBlacklisted ?? 0}
          icon={ShieldX}
          tone="danger"
          hint="in newest-100 sample"
        />
        <StatCard
          label="Orphans (recent)"
          value={overview.isPending ? "—" : overview.data?.kpis.recentOrphans ?? 0}
          icon={Unlink}
          tone="warn"
          hint="contacts not on any list"
        />
      </section>

      {/* Two-column: lists + contact preview */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className={selectedListId !== null ? "lg:col-span-3" : "lg:col-span-5"}>
          <SectionCard
            title="Lists"
            subtitle="Click a row to view its contacts"
            icon={Folder}
            padded={false}
          >
            {overview.isPending ? (
              <ListsSkeleton />
            ) : overview.data?.lists.length ? (
              <ListsTable
                lists={overview.data.lists}
                selectedId={selectedListId}
                onSelect={(id) => setSelectedListId(selectedListId === id ? null : id)}
              />
            ) : (
              <EmptyState icon={List} title="No lists in Brevo yet" />
            )}
          </SectionCard>
        </div>

        {selectedListId !== null && (
          <div className="lg:col-span-2">
            <ContactsPanel listId={selectedListId} onClose={() => setSelectedListId(null)} />
          </div>
        )}
      </div>

      {/* Insight strip */}
      {overview.data && (
        <div className="card p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-info-soft text-blue-700">
              <TrendingUp className="h-4 w-4" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-ink">About the "reported vs actual" gap</div>
              <p className="mt-1 text-xs text-muted">
                Brevo's list-meta endpoint returns a cached <code className="font-mono">totalSubscribers</code> that
                doesn't refresh when contacts are added via integrations (WooCommerce, Conversations, WhatsApp).
                The platform reads the actual count via the list-contacts endpoint instead — that's the number to trust
                before any campaign send.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ListsTable({
  lists,
  selectedId,
  onSelect,
}: {
  lists: AudienceListView[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line bg-canvas/40 text-2xs font-semibold uppercase tracking-wider text-muted">
            <th className="px-5 py-2.5 text-left font-semibold">Name</th>
            <th className="px-3 py-2.5 text-right font-semibold">Reported</th>
            <th className="px-3 py-2.5 text-right font-semibold">Actual</th>
            <th className="px-3 py-2.5 text-right font-semibold">Blacklisted</th>
            <th className="px-3 py-2.5 text-right font-semibold" />
          </tr>
        </thead>
        <tbody>
          {lists.map((l) => {
            const mismatch = l.reportedSubscribers !== l.actualSubscribers;
            const selected = selectedId === l.id;
            return (
              <tr
                key={l.id}
                onClick={() => onSelect(l.id)}
                className={
                  selected
                    ? "cursor-pointer border-b border-line bg-brand-50/60 last:border-b-0"
                    : "cursor-pointer border-b border-line transition last:border-b-0 hover:bg-canvas/60"
                }
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    {l.isMaster && <Star className="h-3.5 w-3.5 text-warn" fill="currentColor" />}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-ink">{l.name}</div>
                      <div className="text-2xs text-muted">id={l.id}{l.folderId ? ` · folder ${l.folderId}` : ""}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-right font-mono text-2xs text-muted">{l.reportedSubscribers.toLocaleString()}</td>
                <td className="px-3 py-3 text-right">
                  <span className={`font-mono text-xs font-semibold ${mismatch ? "text-emerald-700" : "text-ink"}`}>
                    {l.actualSubscribers.toLocaleString()}
                  </span>
                  {mismatch && (
                    <div className="mt-0.5 inline-flex items-center gap-0.5 text-2xs text-warn" title="Brevo's reported count is stale">
                      <AlertCircle className="h-2.5 w-2.5" />
                      mismatch
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-right font-mono text-2xs text-muted">{l.totalBlacklisted.toLocaleString()}</td>
                <td className="px-3 py-3 text-right text-2xs text-brand-700 font-medium">
                  {selected ? "Hide ↑" : "View →"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ListsSkeleton() {
  return (
    <ul className="divide-y divide-line">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="flex items-center justify-between px-5 py-3">
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-2 w-1/4" />
          </div>
          <Skeleton className="h-3 w-16" />
        </li>
      ))}
    </ul>
  );
}

function ContactsPanel({ listId, onClose }: { listId: number; onClose: () => void }) {
  const [offset, setOffset] = useState(0);
  const LIMIT = 25;

  const { data, isPending, error } = useQuery({
    queryKey: ["audience", "contacts", { listId, offset }],
    queryFn: () => api.listAudienceContacts({ listId, limit: LIMIT, offset }),
  });

  return (
    <SectionCard
      title={`Contacts in list ${listId}`}
      subtitle={data ? `${data.count.toLocaleString()} total` : ""}
      icon={Mail}
      padded={false}
      action={
        <button onClick={onClose} className="btn-ghost btn-sm">
          Close
        </button>
      }
    >
      {error ? (
        <div className="px-5 py-4 text-xs text-danger">{(error as Error).message}</div>
      ) : isPending ? (
        <ListsSkeleton />
      ) : data && data.contacts.length > 0 ? (
        <>
          <ul className="divide-y divide-line">
            {data.contacts.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 px-5 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs text-ink">{c.email}</div>
                  {(c.firstName || c.lastName) && (
                    <div className="truncate text-2xs text-muted">{[c.firstName, c.lastName].filter(Boolean).join(" ")}</div>
                  )}
                </div>
                {c.blacklisted && (
                  <span className="pill-danger inline-flex items-center gap-1">
                    <ShieldX className="h-3 w-3" />
                    Blocked
                  </span>
                )}
              </li>
            ))}
          </ul>
          <footer className="flex items-center justify-between gap-2 border-t border-line px-5 py-3 text-2xs text-muted">
            <span>
              {offset + 1}–{Math.min(offset + LIMIT, data.count)} of {data.count}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                disabled={offset === 0}
                className="btn-ghost btn-sm"
              >
                Prev
              </button>
              <button
                onClick={() => setOffset(offset + LIMIT)}
                disabled={offset + LIMIT >= data.count}
                className="btn-ghost btn-sm"
              >
                Next
              </button>
            </div>
          </footer>
        </>
      ) : (
        <EmptyState icon={Users} title="No contacts in this list" />
      )}
    </SectionCard>
  );
}
